import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Container, TextField, Button, Box, Typography } from '@mui/material';
import {decryptKBItem, decryptKBItemIfEncrypted, encryptKBItem, getItemImageUrl} from "../App/cryptoUtils.js";
import { getKB } from "../API/KB/KBAPI.js";
import { useNavigate, useParams } from "react-router-dom";
import {colorText, colorTextInactive, mobileButton} from "../App/theme.js";
import LocalLoader from "../Loaders/LocalLoader.js";
import { createEmbeddingItem } from "../API/OpenAIAPI.js";
import {getQueryParamValue, isMobile} from "../App/utils.js";
import { BackButton } from "../KB/BackButton.js";
import {renderTemplate} from "./ItemHelpers";
import {PaperWrapper} from "../Components/PaperWrapper";
import {backButtonMt, buttonVerticalMargin, subtitleMarginBottom, subtitleMarginTop, titleMt} from "../App/style";
import {RenderInputField} from "./ItemField";
import {FieldBody} from "./Field/body";
import {getItem, createItem, updateItem} from "../API/items/itemsAPI";
import {toYAML} from "../utils";
import {spaLogger} from "../API/APP/SPALoggerAPI";

const Item = ({ kbIdAsSubdomain, injectedItemId }) => {
    const [item, setItem] = useState({});
    let kbId = useParams().kbId;
    if (kbIdAsSubdomain) kbId = kbIdAsSubdomain;
    const [itemIdToCreate, setItemIdToCreate] = useState(null);
    const [isLoading, setLoading] = useState(false);
    const [KBData, setKBData] = useState(null);
    const buttonSubmitItemRef = useRef();

    const [attributes, setAttributes] = useState([]);
    const [embeddingTemplate, setEmbeddingTemplate] = useState(null);
    const [itemType, setItemType] = useState(getQueryParamValue(`type`));

    let itemId = useParams().itemId;
    if (injectedItemId) itemId = injectedItemId;

    const navigate = useNavigate();

    useEffect(() => {
        const fetchData = async () => {
            try {
                const KB = await getKB();
                setKBData(KB);

                let itemTypeData;

                if (itemId) {
                    let itemResponse = await getItem({itemId, KBData: KB});
                    itemTypeData = KB.itemTypes?.[itemResponse?.meta.itemType];

                    Object.keys(itemResponse.item).forEach(attrName => {
                        const attribute = itemTypeData.attributes.find(attr => attr.attrName === attrName);
                        itemResponse.item[attrName] = attribute?.encrypted
                            ? decryptKBItemIfEncrypted(itemResponse.item[attrName])
                            : itemResponse.item[attrName];
                    })

                    setItem(itemResponse);
                    setItemType(itemResponse?.meta.itemType);

                } else {
                    itemTypeData = KB.itemTypes?.[itemType];
                }


                if (!itemTypeData?.attributes) return; // no attributes found (handle items only with body)

                setAttributes(itemTypeData.attributes);
                setEmbeddingTemplate(itemTypeData.embeddingTemplate);
            } catch (error) {
                spaLogger(error.toString())
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);


    useEffect(() => {
        function handleKeyDown(event) {
            if (event.ctrlKey || event.metaKey) {
                switch (String.fromCharCode(event.which).toLowerCase()) {
                    case 's':
                        event.preventDefault();
                        if(buttonSubmitItemRef.current) buttonSubmitItemRef.current.click();
                        break;
                }
            }
        }

        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, []);

    const handleSubmit = async () => {
        try {
            setLoading(true);
            if (itemId) {
                await updateItem({
                    KBData,
                    itemType,
                    itemId: itemId,
                    attributes: attributes.map(({attrType, attrName, encrypted}) => ({attrType, attrName, encrypted})),
                    item: item.item
                });
            } else {
                const { itemId } = await createItem({
                    KBData,
                    itemType,
                    itemId: itemIdToCreate,
                    attributes: attributes.map(({attrType, attrName, encrypted}) => ({attrType, attrName, encrypted})),
                    item: item.item
                })

                navigate(`/edit-item/` + itemId);
            }

        } catch (error) {
            alert(error);
            console.error('Error saving item:', error);
        } finally {
            setLoading(false);
        }
    };

    const onFieldChange = (value, attr) => {
        setItem(prevItem => {
            return {
                ...prevItem,
                item: {
                    ...prevItem?.item,
                    [attr.attrName]: value
                }
            }
        })

    }

    if (!KBData || itemId && !itemType && !getQueryParamValue('fullscreen')) return null;

    return (
        <Container>
            <LocalLoader open={isLoading} blocking={true} />
            <Box mt={backButtonMt} display="flex" justifyContent="space-between">
                <BackButton backToItems={true} />
            </Box>

            <form onSubmit={(e) => {
                e.preventDefault();
                handleSubmit();
            }}>

                <Box mt={titleMt}
                     display="flex"
                     justifyContent={isMobile ? "center" : "flex-start"}
                >
                    <Typography variant="h4" style={{ color: colorText }}>{itemId ? 'Edit ' + (itemType || 'Item') : 'Create '  + (itemType || 'Item')}</Typography>
                </Box>

                <Box
                    display="flex"
                    justifyContent={isMobile ? "center" : "flex-start"}
                    style={{marginBottom: subtitleMarginBottom, marginTop: subtitleMarginTop}}
                >
                    <Typography variant="subtitle1" style={{ color: colorTextInactive }}>
                        {itemId ? 'Edit an' : 'Create a new'} item in the database by unique ID and typed attributes. This item will also be indexed for advanced search capabilities.
                    </Typography>
                </Box>

                <PaperWrapper>

                    {/*// EDIT ITEM*/}
                    {!!itemId && <Box mt={1} style={{paddingBottom: 8}} >
                        <TextField
                            disabled={!!itemId}
                            autoComplete="off"
                            label={attributes.find(o => o.attrType === 'itemId')?.label}
                            value={itemId || itemIdToCreate || ''}
                            // onChange={(e) => setItemIdToCreate(e.target.value)}
                            fullWidth
                            variant="outlined"
                            placeholder="empty"
                        />
                    </Box>}

                    {!attributes?.length && item?.body && <FieldBody {...{ body: decryptKBItem(item?.meta?.body) }} /> }

                    {attributes.map((attr, index) => {
                        // itemId NOT editable
                        if (!!itemId && attr?.attrType === 'itemId') return null;
                        let imageItem = getItemImageUrl(item);

                        return (
                            <>
                            <Box key={index} mt={4} display="flex" alignItems="center" >
                                <RenderInputField {...{ item: item.item, attr, onChange: onFieldChange }} />
                            </Box>
                                {imageItem && attr.attrName === imageItem.imageUrlAttributeName && imageItem.imageUrl &&
                                    <img style={{marginTop: 10}} width={'100%'} src={imageItem.imageUrl} />}
                            </>
                        );
                    })}

                </PaperWrapper>

                <Box mt={0} display="flex" justifyContent={isMobile ? "center" : "flex-end"}>
                    <Button type="submit"
                            ref={buttonSubmitItemRef}
                            variant="outlined"
                            color="secondary"
                            style={{ ...mobileButton, marginTop: buttonVerticalMargin }}
                    >
                        {itemId ? 'Save' : 'Create'}
                    </Button>
                </Box>
            </form>
        </Container>
    );
};

const MemoizedItem = React.memo(({ kbIdAsSubdomain, injectedItemId }) => {
    return useMemo(() => {
        return <Item kbIdAsSubdomain={kbIdAsSubdomain} injectedItemId={injectedItemId} />;
    }, [kbIdAsSubdomain, injectedItemId]);
});

export default MemoizedItem;

// export default Item;